import React, { useMemo } from 'react';
import { Checkbox, FormControlLabel, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedPlural } from 'react-intl';
import classnames from 'classnames';
import { useMessage, useMessages } from 'modules/intl';
import { ExpandCollapseAllButton } from 'modules/common/components/ExpandCollapseAllButton';
import { useAutoPilotContext } from 'modules/App/menu/items/Settings/AutoPilotContextProvider';
import { useFeatureFlag } from 'modules/common/hooks';
import { SmartDiaryEntry } from '../type';
import { useSmartDiaryCheckboxIcons } from '../hooks/useSmartDiaryCheckboxIcons';
import { useSmartDiaryTotals } from './hooks';

type SmartDiaryListHeaderProps = {
  smartEntries: SmartDiaryEntry[];
  onExpandCollapseAll: (state: boolean) => void;
  expandCollapseAllId: string;
  className?: string;
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    width: '100%',
    justifyContent: ({ isAutoPilotEnabled }: { isAutoPilotEnabled: boolean }) =>
      isAutoPilotEnabled ? 'flex-end' : 'space-between',
    padding: theme.spacing(0, 0.125, 0, 3.375),
    color: theme.palette.grey[900]
  },
  durationContainer: {
    display: 'flex',
    paddingRight: theme.spacing(1)
  },
  duration: {
    alignSelf: 'center',
    ...theme.typography.body2,
    padding: theme.spacing(1, 0)
  }
}));

const useExpandStyles = makeStyles((theme) => ({
  expandCollapse: {
    margin: theme.spacing(0, 0.25),
    padding: theme.spacing(0, 0, 0, 0.5)
  }
}));

const useCheckBoxStyles = makeStyles((theme) => ({
  root: {
    color: theme.palette.grey[400],
    padding: theme.spacing(0.875, 1),
    '&:hover': {
      color: theme.palette.grey[500]
    },
    '&.Mui-checked': {
      color: theme.palette.grey[700]
    },
    '&.MuiCheckbox-indeterminate': {
      color: theme.palette.grey[700]
    },
    '&.Mui-checked:hover': {
      color: theme.palette.grey[700]
    }
  },
  disabled: {
    opacity: 0.4
  }
}));

const useCheckBoxControlStyles = makeStyles((theme) => ({
  label: {
    color: ({ checkedCount }: { checkedCount: number }) =>
      checkedCount === 0
        ? theme.palette.text.secondary
        : theme.palette.text.primary,
    ...theme.typography.body2,
    fontStyle: ({ checkedCount }: { checkedCount: number }) =>
      checkedCount === 0 ? 'italic' : 'normal',
    paddingLeft: theme.spacing(0.5)
  }
}));

const messageIds = [
  'smartDiary.selection.zero',
  'smartDiary.selection.one',
  'smartDiary.selection.more',
  'smartDiary.total'
];
export const SmartDiaryListHeader: React.FC<SmartDiaryListHeaderProps> = ({
  smartEntries,
  onExpandCollapseAll,
  expandCollapseAllId,
  className
}: SmartDiaryListHeaderProps) => {
  const smartEntryMap = useMemo(
    () =>
      smartEntries.reduce((acc, value) => {
        if (value.canBeSynced) {
          return { ...acc, [value.id]: value };
        }
        return acc;
      }, {} as Record<string, SmartDiaryEntry>),
    [smartEntries]
  );

  const {
    checked,
    indeterminate,
    checkedCount,
    totalDuration,
    onChange,
    disabled,
    fullhoursMinuteDuration
  } = useSmartDiaryTotals(smartEntryMap);
  const isTiwScreenReaderEnabled = useFeatureFlag('isTiwScreenReaderEnabled');
  const selectMsg = useMessage('noteSelector.selectAllNotes', {
    duration: fullhoursMinuteDuration
  });

  const checkBoxInputProps = useMemo(
    () => ({
      'data-testid': 'clickable_smart-diary-selected-checkbox',
      'aria-label': isTiwScreenReaderEnabled ? selectMsg : ''
    }),
    [isTiwScreenReaderEnabled, selectMsg]
  );
  const { isAutoPilotEnabled } = useAutoPilotContext();
  const classes = useStyles({ isAutoPilotEnabled });
  const checkBoxStyles = useCheckBoxStyles();
  const expandClasses = useExpandStyles();

  const checkBoxControlStyles = useCheckBoxControlStyles({
    checkedCount
  });
  const messageValues = useMemo(
    () => ({
      count: checkedCount
    }),
    [checkedCount]
  );
  const iconProps = useSmartDiaryCheckboxIcons();
  const messages = useMessages(messageIds, messageValues);
  const checkBoxLabel = useMemo(() => {
    if (!checkedCount) {
      return messages['smartDiary.selection.zero'];
    }
    return (
      <FormattedPlural
        one={messages['smartDiary.selection.one']}
        other={messages['smartDiary.selection.more']}
        value={checkedCount}
      />
    );
  }, [checkedCount, messages]);
  const formControlLabelMemo = useMemo(() => {
    if (!isAutoPilotEnabled)
      return (
        <FormControlLabel
          classes={checkBoxControlStyles}
          labelPlacement="end"
          control={
            <Checkbox
              classes={checkBoxStyles}
              data-testid="smart-diary-selected-checkbox"
              color="default"
              checked={Boolean(checked)}
              indeterminate={indeterminate}
              onChange={onChange}
              inputProps={checkBoxInputProps}
              disabled={disabled}
              {...iconProps}
            />
          }
          label={checkBoxLabel}
        />
      );
    else return <div></div>;
  }, [
    checkBoxControlStyles,
    checkBoxInputProps,
    checkBoxLabel,
    checkBoxStyles,
    checked,
    disabled,
    iconProps,
    indeterminate,
    isAutoPilotEnabled,
    onChange
  ]);

  return (
    <div
      className={classnames(classes.root, className)}
      data-testid="smart-diary-total"
    >
      {formControlLabelMemo}
      <div className={classes.durationContainer}>
        <Typography
          className={classes.duration}
          data-testid="smart-diary-total-duration"
          variant="body1"
        >
          {totalDuration}
        </Typography>
        <ExpandCollapseAllButton
          id={expandCollapseAllId}
          onExpandCollapseAll={onExpandCollapseAll}
          overrideClass={expandClasses}
          fullhoursMinuteDuration={fullhoursMinuteDuration}
        />
      </div>
    </div>
  );
};
